import React from 'react'
import { EnvelopeSimple, Key, ShieldCheck } from '@phosphor-icons/react'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import TimedToastAlert from '../../components/alerts/TimedToastAlert'
import { UI_COLOR_PRIMARY_SOFT, UI_COLOR_SECONDARY_MAIN } from '../../constants/uiColors'
import {
  LOGIN_FIELD_SX,
  LOGIN_REMEMBER_CHECKBOX_SX,
  LOGIN_REMEMBER_LABEL_SX
} from './loginConstants'

const MFA_CODE_LENGTH = 6

export default function MfaLoginForm({
  otpCode,
  setOtpCode,
  recoveryCode,
  setRecoveryCode,
  isSubmitting,
  onSubmit,
  onBack,
  useRecoveryCode = false,
  onToggleRecoveryCode,
  rememberDevice = true,
  setRememberDevice,
  rememberDeviceAvailable = false,
  rememberDeviceDays = 0,
  verificationMethod = 'totp',
  emailCodeSent = false,
  destinationHint = '',
  totpAvailable = false,
  emailAvailable = false,
  onUseAuthenticator,
  onUseEmailCode,
  onResendEmailCode,
  resendBusy = false,
  formAlertId,
  formAlertSeverity = 'error',
  formAlertMessage,
  onClearFormAlert,
  fieldSx = LOGIN_FIELD_SX
}) {
  const usingEmailCode = !useRecoveryCode && verificationMethod === 'email'
  const codeLabel = useRecoveryCode
    ? 'Código de recuperación'
    : usingEmailCode
      ? 'Código del correo'
      : 'Código de autenticación'
  const codeHelper = useRecoveryCode
    ? 'Ingresa uno de tus códigos de respaldo de un solo uso.'
    : usingEmailCode && emailCodeSent && destinationHint
      ? `Te enviamos un código a ${destinationHint}.`
      : ''
  const canSubmit = useRecoveryCode ? recoveryCode.trim().length > 0 : /^\d{6}$/.test(otpCode)
  const CodeIcon = useRecoveryCode ? Key : ShieldCheck

  if (!verificationMethod && totpAvailable && emailAvailable) {
    return (
      <div className="login-mfa-choice">
        <button
          type="button"
          className="login-mfa-option"
          onClick={onUseEmailCode}
          disabled={resendBusy}
        >
          <span className="login-mfa-option-icon">
            <EnvelopeSimple size={24} aria-hidden="true" />
          </span>
          <span className="login-mfa-option-copy">
            <strong>{resendBusy ? 'Enviando código…' : 'Enviar código por correo'}</strong>
            <span>{destinationHint || 'Recibirás un código en tu correo.'}</span>
          </span>
        </button>
        <button
          type="button"
          className="login-mfa-option"
          style={{
            '--login-mfa-option-accent': UI_COLOR_SECONDARY_MAIN,
            '--login-mfa-option-soft': UI_COLOR_PRIMARY_SOFT
          }}
          onClick={onUseAuthenticator}
          disabled={resendBusy}
        >
          <span className="login-mfa-option-icon">
            <ShieldCheck size={24} aria-hidden="true" />
          </span>
          <span className="login-mfa-option-copy">
            <strong>Aplicación de verificación</strong>
            <span>Usa el código de Google Authenticator o similar.</span>
          </span>
        </button>
        {formAlertMessage ? (
          <div className="login-form-alert" aria-live="polite">
            <TimedToastAlert
              id={formAlertId}
              variant="inline"
              severity={formAlertSeverity}
              message={formAlertMessage}
              onClose={onClearFormAlert}
            />
          </div>
        ) : null}
        <button type="button" className="login-forgot-link login-mfa-choice-back" onClick={onBack}>
          Volver al inicio de sesión
        </button>
      </div>
    )
  }

  const handleCodeChange = (event) => {
    if (useRecoveryCode) {
      setRecoveryCode(event.target.value)
      return
    }

    setOtpCode(event.target.value.replace(/\D/g, '').slice(0, MFA_CODE_LENGTH))
  }

  const handleCodePaste = (event) => {
    if (useRecoveryCode) return

    event.preventDefault()
    const input = event.target
    const pastedDigits = event.clipboardData.getData('text').replace(/\D/g, '')
    const start = input.selectionStart ?? otpCode.length
    const end = input.selectionEnd ?? start
    setOtpCode(
      `${otpCode.slice(0, start)}${pastedDigits}${otpCode.slice(end)}`.slice(0, MFA_CODE_LENGTH)
    )
  }

  return (
    <>
      <form className="login-main-form" onSubmit={onSubmit} noValidate>
        <TextField
          id="login-mfa-code"
          label={codeLabel}
          variant="outlined"
          fullWidth
          name="otp"
          value={useRecoveryCode ? recoveryCode : otpCode}
          onChange={handleCodeChange}
          onPaste={handleCodePaste}
          autoComplete="one-time-code"
          autoFocus
          required
          helperText={codeHelper}
          sx={{
            ...fieldSx,
            '& .MuiOutlinedInput-input': {
              ...fieldSx['& .MuiOutlinedInput-input'],
              letterSpacing: '0.04em',
              fontWeight: 600
            }
          }}
          slotProps={{
            htmlInput: useRecoveryCode
              ? {
                  inputMode: 'text',
                  maxLength: 64,
                  spellCheck: false,
                  autoCorrect: 'off',
                  autoCapitalize: 'characters'
                }
              : {
                  inputMode: 'numeric',
                  maxLength: MFA_CODE_LENGTH,
                  pattern: '[0-9]*'
                },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <CodeIcon className="login-field-icon" size={18} />
                </InputAdornment>
              )
            }
          }}
        />

        {formAlertMessage ? (
          <div className="login-form-alert" aria-live="polite">
            <TimedToastAlert
              id={formAlertId}
              variant="inline"
              severity={formAlertSeverity}
              message={formAlertMessage}
              onClose={onClearFormAlert}
            />
          </div>
        ) : null}

        {rememberDeviceAvailable ? (
          <div className="login-form-row login-mfa-remember-row">
            <FormControlLabel
              className="login-remember"
              control={
                <Checkbox
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  size="small"
                  sx={LOGIN_REMEMBER_CHECKBOX_SX}
                />
              }
              label={`No pedir código en este dispositivo por ${rememberDeviceDays} día${
                rememberDeviceDays === 1 ? '' : 's'
              }`}
              sx={LOGIN_REMEMBER_LABEL_SX}
            />
          </div>
        ) : null}

        <button
          className={`login-submit-btn${isSubmitting ? ' is-loading' : ''}`}
          type="submit"
          disabled={isSubmitting || !canSubmit}
        >
          <span className="login-submit-label">Verificar e iniciar sesión</span>
          <span className="login-submit-spinner" aria-hidden="true" />
        </button>
      </form>

      <div className="login-form-footer login-mfa-footer">
        {usingEmailCode && emailCodeSent && typeof onResendEmailCode === 'function' ? (
          <button
            type="button"
            className="login-forgot-link login-mfa-toggle"
            onClick={onResendEmailCode}
            disabled={resendBusy || isSubmitting}
          >
            Reenviar código
          </button>
        ) : null}
        {usingEmailCode && totpAvailable && typeof onUseAuthenticator === 'function' ? (
          <button
            type="button"
            className="login-forgot-link login-mfa-toggle"
            onClick={onUseAuthenticator}
          >
            Usar aplicación de verificación
          </button>
        ) : null}
        {!usingEmailCode && emailAvailable && typeof onUseEmailCode === 'function' ? (
          <button
            type="button"
            className="login-forgot-link login-mfa-toggle"
            onClick={onUseEmailCode}
          >
            Usar código del correo
          </button>
        ) : null}
        {totpAvailable && typeof onToggleRecoveryCode === 'function' ? (
          <button
            type="button"
            className="login-forgot-link login-mfa-toggle"
            onClick={onToggleRecoveryCode}
          >
            {useRecoveryCode ? 'Usar código de autenticación' : 'Usar código de recuperación'}
          </button>
        ) : null}
        <button type="button" className="login-forgot-link" onClick={onBack}>
          Volver al inicio de sesión
        </button>
      </div>
    </>
  )
}
